import { TextFieldModule } from '@angular/cdk/text-field';
import { CurrencyPipe, DOCUMENT, Location, NgClass, NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { Router, RouterModule } from '@angular/router';
import { WINDOW } from '@ng-web-apis/common';
import { I18NEXT_SERVICE, I18NextModule, ITranslationService } from 'angular-i18next';
import { mergeMap } from 'rxjs';
import { BaseComponent } from 'src/app/_shared/components/base/base.component';
import { ContactUsButtonComponent } from 'src/app/_shared/components/contact-us-button/contact-us-button.component';
import { IconComponent } from 'src/app/common/icon/icon.component';
import { METADATA_SERVICE, MetadataService } from 'src/app/common/metadata/metadata.service';
import { OpenSnackBarService } from 'src/app/common/open-snack-bar/open-snack-bar.service';
import { RouteTranslatePipe } from 'src/app/_shared/pipes/route-translate.pipe';
import { RouteTranslateService } from 'src/app/common/route-translate/route-translate.service';
import { navigateToExternalUrl } from 'src/app/common/utils/helpers';
import { GiftCardCreateInput } from 'src/types/graphql';
import { FooterComponent } from '../../_shared/components/footer/footer.component';
import { HeaderComponent } from '../../_shared/components/header/header.component';
import { CartService } from '../cart/cart.service';
import { GiftCardCreateGQL } from './gift-card.mutation.generated';

@Component({
  selector: 'sharry-gift-card',
  standalone: true,
  templateUrl: './gift-card.component.html',
  styleUrls: ['./gift-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    CurrencyPipe,
    NgStyle,
    I18NextModule,
    ReactiveFormsModule,
    FormsModule,
    MatInputModule,
    MatSelectModule,
    MatButtonToggleModule,
    MatRadioModule,
    TextFieldModule,
    MatDatepickerModule,
    IconComponent,
    MatButtonModule,
    MatExpansionModule,
    HeaderComponent,
    FooterComponent,
    RouterModule,
    RouteTranslatePipe,
    ContactUsButtonComponent,
  ],
})
export class GiftCardComponent extends BaseComponent implements OnInit, OnDestroy {
  descriptions: { key: number; value: string }[] = [
    { key: 0, value: 'gift-card.description.0' },
    { key: 1, value: 'gift-card.description.1' },
    { key: 2, value: 'gift-card.description.2' },
    { key: 3, value: 'gift-card.description.3' },
  ];
  amounts: number[] = [10, 15, 20, 50, 100, 250, 500, 1500];
  cards: { key: number; imgUrl: string; active: boolean }[] = [
    { key: 0, imgUrl: 'https://media.sharry.land/app/images/gift-card/01-gift-card.webp', active: true },
    { key: 1, imgUrl: 'https://media.sharry.land/app/images/gift-card/02-gift-card.webp', active: false },
    { key: 2, imgUrl: 'https://media.sharry.land/app/images/gift-card/03-gift-card.webp', active: false },
    { key: 3, imgUrl: 'https://media.sharry.land/app/images/gift-card/04-gift-card.webp', active: false },
  ];
  today: Date = new Date();
  form: FormGroup<{
    design: FormControl<number>;
    description: FormControl<number>;
    amount: FormControl<number>;
    ship: FormControl<number>;
    recipientName: FormControl<string>;
    recipientMail: FormControl<string>;
    message: FormControl<string>;
    sender: FormControl<string>;
    when: FormControl<Date>;
  }>;
  giftcardExampleUrl = 'https://media.sharry.land/app/pdf/gift_card_example.pdf';

  constructor(
    private fb: FormBuilder,
    private giftCardCreateGQL: GiftCardCreateGQL,
    private router: Router,
    private routeTranslateService: RouteTranslateService,
    private cartService: CartService,
    private renderer: Renderer2,
    private openSnackBarService: OpenSnackBarService,
    @Inject(I18NEXT_SERVICE) private translateService: ITranslationService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(WINDOW) private window: Window,
    @Inject(METADATA_SERVICE) private metadataService: MetadataService,
    private location: Location,
  ) {
    super();
    this.metadataService.setMetadataPage('gift-card', this.location.path());
    this.form = this.fb.group({
      design: this.fb.control<number>(1, Validators.required),
      description: this.fb.control<number>(0, Validators.required),
      amount: this.fb.control<number>(50, Validators.required),
      ship: this.fb.control<number>(0, Validators.required),
      recipientName: this.fb.control<string>('', Validators.required),
      recipientMail: this.fb.control<string>('', [Validators.required, Validators.email]),
      message: this.fb.control<string>(''),
      sender: this.fb.control<string>('', Validators.required),
      when: this.fb.control<Date>(new Date(), Validators.required),
    });
  }

  ngOnInit(): void {
    this.renderer.addClass(this.document.querySelector('html'), 'shrink');
  }

  onCardClick(index: number) {
    this.cards.forEach((c, i) => {
      if (i === index) {
        c.active = true;
        this.form.get('design')?.setValue(c.key);
      } else {
        c.active = false;
      }
    });
  }

  setAmount(event: any) {
    this.form
      .get('amount')
      ?.setValue(event?.target?.value ? parseInt(event?.target?.value) : typeof event === 'number' ? event : 50);
  }

  addToCart() {
    const input: GiftCardCreateInput = {
      cardMessage: this.form.get('description')?.value,
      deliveredAt: this.form.get('when')?.value,
      template: this.form.get('design')?.value,
      email: this.form.get('recipientMail')?.value,
      message: this.form.get('message')?.value,
      deliveryMethod: this.form.get('ship')?.value,
      price: this.form.get('amount')?.value,
      receiverName: this.form.get('recipientName')?.value,
      senderName: this.form.get('sender')?.value,
    };
    this.subscriptions.push(
      this.cartService
        .getCartUuid(true)
        .pipe(mergeMap(uuid => this.giftCardCreateGQL.mutate({ input: input, cartUuid: uuid })))
        .subscribe(() => this.router.navigate([this.routeTranslateService.translateRoute('cart')])),
    );
  }

  onDownloadPdf() {
    navigateToExternalUrl(this.window, this.giftcardExampleUrl, this.openSnackBarService, this.translateService);
  }

  ngOnDestroy(): void {
    this.renderer.removeClass(this.document.querySelector('html'), 'shrink');
  }
}
